/**
 * Appointment Maps JavaScript
 * Handles Google Maps integration for appointments
 */

// Track if Google Maps is being loaded to prevent multiple loads
let isGoogleMapsLoading = false;
let googleMapsLoadPromise = null;

// Dynamic Google Maps loader function
function loadGoogleMapsForAppointments(screenings, mapElementId = 'appointment-map') {
    const apiKey = window.google_map_key;
    if (!apiKey) {
        const mapElement = document.getElementById('appointment-map');
        if (mapElement) {
            mapElement.innerHTML =
                '<div style="display: flex; align-items: center; justify-content: center; height: 100%; background: #f5f5f5; color: #666;">Google Maps API key not configured.</div>';
        }
        return;
    }

    // Check if Google Maps is already loaded
    if (typeof google !== 'undefined' && google.maps && google.maps.Map) {
        initializeAppointmentMap(screenings, mapElementId);
        return;
    }

    // Check if Google Maps is already being loaded
    if (isGoogleMapsLoading && googleMapsLoadPromise) {
        googleMapsLoadPromise.then(() => {
            initializeAppointmentMap(screenings, mapElementId);
        });
        return;
    }

    // Check if script tag already exists
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
        // Script already exists, wait for it to load
        const checkGoogleMaps = () => {
            if (typeof google !== 'undefined' && google.maps && google.maps.Map) {
                initializeAppointmentMap(screenings, mapElementId);
            } else {
                setTimeout(checkGoogleMaps, 100);
            }
        };
        checkGoogleMaps();
        return;
    }

    // Set loading flag
    isGoogleMapsLoading = true;

    // Create unique callback name
    const callbackName = `initAppointmentMap_${Date.now()}`;

    // Define the callback function
    window[callbackName] = function () {
        isGoogleMapsLoading = false;

        // Double-check that Map constructor is available before proceeding
        if (!google.maps || !google.maps.Map) {
            // Retry after a short delay
            setTimeout(() => {
                if (google.maps && google.maps.Map) {
                    initializeAppointmentMap(screenings, mapElementId);
                }
            }, 100);
            return;
        }

        initializeAppointmentMap(screenings, mapElementId);
    };

    // Create and append script tag
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=${callbackName}&loading=async&v=weekly&libraries=marker`;
    script.async = true;
    script.defer = false;

    script.onerror = function () {
        isGoogleMapsLoading = false;
        const mapElement = document.getElementById(mapElementId);
        if (mapElement) {
            mapElement.innerHTML =
                '<div style="display: flex; align-items: center; justify-content: center; height: 100%; background: #f5f5f5; color: #666;">Failed to load Google Maps.</div>';
        }
    };

    document.head.appendChild(script);
}

// Initialize the actual map
function initializeAppointmentMap(screenings, mapElementId = 'appointment-map') {
    const mapElement = document.getElementById(mapElementId);
    if (!mapElement) {
        return;
    }

    if (typeof google === 'undefined') {
        return;
    }

    if (!google.maps || !google.maps.Map) {
        return;
    }

    // Check if the map container is visible (important for mobile)
    const isVisible = mapElement.offsetWidth > 0 && mapElement.offsetHeight > 0;
    if (!isVisible) {
        return;
    }

    try {
        // Find center coordinates
        let centerLat = 39.8283; // Default center of US
        let centerLng = -98.5795;

        for (const screening of screenings) {
            if (screening.latitude && screening.longitude) {
                centerLat = parseFloat(screening.latitude);
                centerLng = parseFloat(screening.longitude);
                break;
            }
        }

        // Create map
        const map = new google.maps.Map(mapElement, {
            zoom: 10,
            center: { lat: centerLat, lng: centerLng },
        });

        // Store map instance for potential resize
        mapElement.mapInstance = map;

        const bounds = new google.maps.LatLngBounds();
        let markersAdded = 0;

        // Add markers
        screenings.forEach((screening, index) => {
            const lat = parseFloat(screening.latitude || 0);
            const lon = parseFloat(screening.longitude || 0);

            if (lat && lon && lat !== 0 && lon !== 0) {
                try {
                    const marker = new google.maps.Marker({
                        position: { lat: lat, lng: lon },
                        map: map,
                        title: screening.siteName || 'Unknown Location',
                        icon: {
                            url: 'https://cdn.shopify.com/s/files/1/0922/0033/7714/files/google-maps-icon-booking-app.svg?v=1751042826',
                            scaledSize: new google.maps.Size(46, 46),
                        },
                    });

                    const address1 = screening.address1 || '';
                    const address2 = screening.address2 || '';
                    const cityZip = `${screening.city || ''}, ${screening.state || ''} ${screening.zipCode || ''}`;
                    const fullAddress = [address1, address2].filter(Boolean).join(', ');
                    const date = screening.screeningDate
                        ? screening.screeningDate.split('T')[0]
                        : 'Date TBD';

                    const infoWindow = new google.maps.InfoWindow({
                        content: `
                            <div class="custom-info-window">
                                <div class="info-window-header">
                                    <h3 class="info-window-title">${screening.siteName || 'Unknown Location'}</h3>
                                </div>
                                <div class="info-window-body">
                                    <div class="info-window-address">
                                        ${fullAddress}<br/>
                                        ${cityZip}
                                    </div>
                                    <div class="info-window-details">
                                        <div class="info-window-date">${formatDate(date)}</div>
                                    </div>
                                </div>
                            </div>
                        `,
                    });

                    marker.addListener('click', function () {
                        infoWindow.open(map, marker);
                    });

                    bounds.extend(marker.getPosition());
                    markersAdded++;
                } catch (error) {
                    // Silent error handling
                }
            }
        });

        if (markersAdded > 0) {
            if (screenings.length === 1) {
                // For single location, use the same zoom level as the default
                map.setCenter({ lat: centerLat, lng: centerLng });
                map.setZoom(10); // Same zoom level as before
            } else {
                // For multiple locations, fit all markers in view
                map.fitBounds(bounds);
            }
        }

        // Trigger resize after a short delay to ensure proper rendering
        setTimeout(() => {
            google.maps.event.trigger(map, 'resize');
            if (markersAdded > 0) {
                if (screenings.length === 1) {
                    // For single location, maintain the same zoom level
                    map.setCenter({ lat: centerLat, lng: centerLng });
                    map.setZoom(10);
                } else {
                    // For multiple locations, fit all markers in view
                    map.fitBounds(bounds);
                }
            }
        }, 200);
    } catch (error) {
        // Silent error handling
    }
}

// Helper function to update maps with current screenings
function updateMapsWithScreenings(screenings) {
    loadGoogleMapsForAppointments(screenings);
    loadGoogleMapsForAppointments(screenings, 'appointment-map-desktop');
}
